import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { useHistory, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Breadcrumbs from 'components/Common/Breadcrumb';
import { CouponCodeApi } from "apis/CouponCodeApi";
import Swal from "../../helpers/swal_helper";
import moment from "moment";
import SalonSelect from "components/Common/SalonSelect";

function AddEditCouponCode(props) {
    const history = useHistory();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [forFirstUser, setForFirstUser] = useState(false);
    const [initialValues, setInitialValues] = useState({
        code: "",
        description: "",
        applicable_for: "All",
        discount_type: "percentage",
        amount: "",
        min_order_value: 0,
        max_discount_amount: 0,
        for_new_user: false,
        usage_limit: 0,
        usage_limit_per_user: 1,
        valid_from: "",
        valid_to: "",
        status: "active",
        salon_ids: []
    });

    const validationSchema = Yup.object().shape({
        code: Yup.string()
            .required(props.t("Code is required"))
            .min(3, props.t("Code must be at least 3 characters"))
            .max(20, props.t("Code must not exceed 20 characters")),
        applicable_for: Yup.string()
            .required(props.t("Applicable for is required")),
        discount_type: Yup.string()
            .required(props.t("Discount type is required")),
        amount: Yup.number()
            .required(props.t("Amount is required"))
            .min(0, props.t("Amount must be greater than or equal to 0"))
            .when('discount_type', {
                is: 'percentage',
                then: Yup.number().max(100, props.t("Percentage cannot exceed 100"))
            }),
            min_order_value: Yup.number()
            // .required(props.t("Minimum order value is required"))
            .min(0, props.t("Minimum order value must be greater than or equal to 0")),
        //   max_discount_amount: Yup.number()
        //     .required(props.t("Maximum discount amount is required"))
        //     .min(0, props.t("Maximum discount amount must be greater than or equal to 0"))
        //     .when('min_order_value', {
        //       is: (minOrderValue) => minOrderValue > 0,
        //       then: Yup.number().min(Yup.ref('min_order_value'), props.t("Maximum discount amount must be greater than or equal to minimum order value"))
        //     }),
        // usage_limit: Yup.number()
        //     .required(props.t("Usage limit is required"))
        //     .min(1, props.t("Usage limit must be at least 1")),
        usage_limit_per_user: Yup.number()
            .required(props.t("Usage limit per user is required"))
            .min(1, props.t("Usage limit per user must be at least 1")),
            valid_from: Yup.date()
            .required(props.t("Valid from date is required")),
          valid_to: Yup.date()
            .required(props.t("Valid to date is required"))
            .when('valid_from', {
              is: (validFrom) => validFrom !== null,
              then: Yup.date().min(Yup.ref('valid_from'), props.t("Valid to date must be after valid from date"))
            }),
        status: Yup.string()
            .required(props.t("Status is required")),
        salon_ids: Yup.array()
            .when('applicable_for', {
                is: 'Selected',
                then: Yup.array().min(1, props.t("Please select at least one salon"))
            })
    });

    useEffect(() => {
        if (id) {
            getCouponCodeById(id);
        }
    }, [id]);

    const getCouponCodeById = (id) => {
        setLoading(true);
        CouponCodeApi.getCouponCodeById({coupon_id : id})
            .then((res) => {
                if (res.data.status) {
                    const data = res.data.data;
                    setInitialValues({
                        ...data,
                        salon_ids: data.salons?.map(s => ({ value: s.id, label: s.name })) || [],
                        valid_from: moment(data.valid_from).format("YYYY-MM-DD"),
                        valid_to: moment(data.valid_to).format("YYYY-MM-DD"),
                        status: data.status ? "active" : "inactive"
                    });
                    setForFirstUser(data.for_new_user === 1);
                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.data.message,
                        icon: "error",
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
                Swal.fire({
                    title: "Error",
                    text: "Failed to fetch coupon code details",
                    icon: "error",
                });
            });
    };

    const handleSubmit = (values, { setSubmitting }) => {
        setLoading(true);

        const submitData = {
            ...values,
            salon_ids: values.salon_ids.map(s => s.value),
            status: values.status === "active",
            for_new_user: forFirstUser
        };

        const apiCall = id
            ? CouponCodeApi.updateCouponCode({
                ...submitData,
                coupon_id: id
            })
            : CouponCodeApi.createCouponCode(submitData);

        apiCall
            .then((res) => {
                if (res.data.status) {
                    Swal.fire({
                        title: "Success",
                        text: res.data.message,
                        icon: "success",
                    }).then(() => {
                        history.push("/coupon-codes");
                    });
                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.data.message,
                        icon: "error",
                    });
                }
                setLoading(false);
                setSubmitting(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
                setSubmitting(false);
                Swal.fire({
                    title: "Error",
                    text: "Failed to save coupon code",
                    icon: "error",
                });
            });
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs
                    title={props.t("Dashboard")}
                    breadcrumbItem={id ? props.t("Edit Coupon Code") : props.t("Add Coupon Code")}
                />
            </div>

            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                    enableReinitialize
                                >
                                    {({ errors, touched, values, setFieldValue }) => (
                                        <Form>
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Code")}</label>
                                                    <Field
                                                        type="text"
                                                        className={`form-control ${errors.code && touched.code ? 'is-invalid' : ''}`}
                                                        name="code"
                                                    />
                                                    {errors.code && touched.code && (
                                                        <div className="invalid-feedback">{errors.code}</div>
                                                    )}
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Applicable For")}</label>
                                                    <Field
                                                        as="select"
                                                        className={`form-select ${errors.applicable_for && touched.applicable_for ? 'is-invalid' : ''}`}
                                                        name="applicable_for"
                                                    >
                                                        <option value="All">{props.t("All")}</option>
                                                        <option value="Selected">{props.t("Selected")}</option>
                                                    </Field>
                                                    {errors.applicable_for && touched.applicable_for && (
                                                        <div className="invalid-feedback">{errors.applicable_for}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Discount Type")}</label>
                                                    <Field
                                                        as="select"
                                                        className={`form-select ${errors.discount_type && touched.discount_type ? 'is-invalid' : ''}`}
                                                        name="discount_type"
                                                    >
                                                        <option value="percentage">{props.t("Percentage")}</option>
                                                        <option value="flat">{props.t("Flat")}</option>
                                                    </Field>
                                                    {errors.discount_type && touched.discount_type && (
                                                        <div className="invalid-feedback">{errors.discount_type}</div>
                                                    )}
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Amount")}</label>
                                                    <Field
                                                        type="number"
                                                        className={`form-control ${errors.amount && touched.amount ? 'is-invalid' : ''}`}
                                                        name="amount"
                                                    />
                                                    {errors.amount && touched.amount && (
                                                        <div className="invalid-feedback">{errors.amount}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Minimum Order Value")}</label>
                                                    <Field
                                                        type="number"
                                                        className={`form-control ${errors.min_order_value && touched.min_order_value ? 'is-invalid' : ''}`}
                                                        name="min_order_value"
                                                    />
                                                    {errors.min_order_value && touched.min_order_value && (
                                                        <div className="invalid-feedback">{errors.min_order_value}</div>
                                                    )}
                                                </div>
                                                {/* <div className="col-md-6">
                                                    <label className="form-label">{props.t("Maximum Discount Amount")}</label>
                                                    <Field
                                                        type="number"
                                                        className={`form-control ${errors.max_discount_amount && touched.max_discount_amount ? 'is-invalid' : ''}`}
                                                        name="max_discount_amount"
                                                    />
                                                    {errors.max_discount_amount && touched.max_discount_amount && (
                                                        <div className="invalid-feedback">{errors.max_discount_amount}</div>
                                                    )}
                                                </div> */}

                                                {/* for first order only */}
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("First Order Only")}</label>
                                                    <Field
                                                        as="select"
                                                        className={`form-select ${errors.for_new_user && touched.for_new_user ? 'is-invalid' : ''}`}
                                                        name="for_new_user"
                                                        onChange={(e) => {
                                                            setFieldValue('for_new_user', e.target.value);
                                                            setForFirstUser(e.target.value === "1" ? true : false);
                                                            if (e.target.value === "1") {
                                                                setFieldValue('usage_limit_per_user', 1);
                                                            }
                                                        }}
                                                    >
                                                        <option value="0">No</option>
                                                        <option value="1">Yes</option>
                                                    </Field>
                                                    {errors.for_new_user && touched.for_new_user && (
                                                        <div className="invalid-feedback">{errors.for_new_user}</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Usage Limit")}</label>
                                                    <Field
                                                        type="number"
                                                        className={`form-control ${errors.usage_limit && touched.usage_limit ? 'is-invalid' : ''}`}
                                                        name="usage_limit"
                                                    />
                                                    {errors.usage_limit && touched.usage_limit && (
                                                        <div className="invalid-feedback">{errors.usage_limit}</div>
                                                    )}
                                                </div>
                                      
                                            </div> */}

                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Valid From")}</label>
                                                    <Field
                                                        type="date"
                                                        className={`form-control ${errors.valid_from && touched.valid_from ? 'is-invalid' : ''}`}
                                                        name="valid_from"
                                                    />
                                                    {errors.valid_from && touched.valid_from && (
                                                        <div className="invalid-feedback">{errors.valid_from}</div>
                                                    )}
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Valid To")}</label>
                                                    <Field
                                                        type="date"
                                                        className={`form-control ${errors.valid_to && touched.valid_to ? 'is-invalid' : ''}`}
                                                        name="valid_to"
                                                    />
                                                    {errors.valid_to && touched.valid_to && (
                                                        <div className="invalid-feedback">{errors.valid_to}</div>
                                                    )}
                                                </div>
                                            </div>

                                            {values.applicable_for === "Selected" && (
                                                <div className="row mb-3">
                                                    <div className="col-md-12">
                                                        <label className="form-label">{props.t("Select Salons")}</label>
                                                        <SalonSelect
                                                            value={values.salon_ids}
                                                            onChange={(selectedOptions) => setFieldValue('salon_ids', selectedOptions)}
                                                            isMulti={true}
                                                            t={props.t}
                                                        />
                                                        {errors.salon_ids && touched.salon_ids && (
                                                            <div className="invalid-feedback d-block">{errors.salon_ids}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="row mb-3">
                                            <div className="col-md-6">
                                                    <label className="form-label">{props.t("Usage Limit Per User")}</label>
                                                    <Field
                                                        type="number"
                                                        className={`form-control ${errors.usage_limit_per_user && touched.usage_limit_per_user ? 'is-invalid' : ''}`}
                                                        name="usage_limit_per_user"
                                                    />
                                                    {errors.usage_limit_per_user && touched.usage_limit_per_user && (
                                                        <div className="invalid-feedback">{errors.usage_limit_per_user}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Status")}</label>
                                                    <Field
                                                        as="select"
                                                        className={`form-select ${errors.status && touched.status ? 'is-invalid' : ''}`}
                                                        name="status"
                                                    >
                                                        <option value="active">{props.t("Active")}</option>
                                                        <option value="inactive">{props.t("Inactive")}</option>
                                                    </Field>
                                                    {errors.status && touched.status && (
                                                        <div className="invalid-feedback">{errors.status}</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* description field */}
                                            <div className="row mb-3">
                                                <div className="col-md-12">
                                                    <label className="form-label">{props.t("Description")}</label>
                                                    <Field
                                                        as="textarea"
                                                        className={`form-control ${errors.description && touched.description ? 'is-invalid' : ''}`}
                                                        name="description"
                                                    />
                                                    {errors.description && touched.description && (
                                                        <div className="invalid-feedback">{errors.description}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-12">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={loading}
                                                    >
                                                        {loading ? props.t("Loading...") : id ? props.t("Update") : props.t("Save")}
                                                    </button>
                                                </div>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

AddEditCouponCode.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AddEditCouponCode);